import isNil from 'lodash-es/isNil'
import pick from 'lodash-es/pick'

import {
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_CONFLICT,
} from '../../constants/api'
import { CalendarAppointmentStatus } from '../../constants/calendar'
import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { sleep } from '../../utils/async'

import {
  APPOINTMENT_MOCK_CREATED_PAYLOAD_FIELDS,
  APPOINTMENT_MOCK_EDITABLE_FIELDS,
  APPOINTMENT_MOCK_FOLLOW_UP_RELEASED,
  APPOINTMENT_MOCK_ID_PREFIX,
  APPOINTMENT_MOCK_LATENCY,
  AppointmentErrorCode,
  AppointmentHistoryAction,
} from './Appointment.constants'
import {
  getAppointmentMockRecord,
  getAppointmentMockRecords,
  getAppointmentMockRequestDueDate,
  saveAppointmentMockRecord,
} from './Appointment.mock.store'
import {
  getAppointmentMockError,
  getAppointmentMockId,
  getAppointmentMockUpdatePrompt,
  getIsAppointmentMockFollowUpLive,
  getIsAppointmentMockSameInstant,
  getIsAppointmentMockTimeValid,
  toAppointmentMockDetail,
  toAppointmentMockHistoryEntry,
  toAppointmentMockRescheduledEntry,
} from './Appointment.mock.utils'
import {
  getAppointmentMockListPage,
  getIsAppointmentMockListParamsValid,
} from './Appointment.mock.utils.list'
import {
  getAppointmentMockUpdatedAnchor,
  toAppointmentMockAnchor,
  toAppointmentMockEmailThread,
  toAppointmentMockParticipants,
} from './Appointment.mock.utils.records'

function checkAppointmentMockTime(record) {
  if (
    !getIsAppointmentMockTimeValid({
      endAt: record.end_at,
      startAt: record.start_at,
    })
  ) {
    throw getAppointmentMockError({ status: HTTP_STATUS_BAD_REQUEST })
  }
}

export function createMockAppointment({ data, mockContext = EMPTY_OBJECT }) {
  return sleep(APPOINTMENT_MOCK_LATENCY).then(() => {
    checkAppointmentMockTime(data)

    const numericId = getAppointmentMockId()

    const organizer = mockContext.user ?? null

    const record = saveAppointmentMockRecord({
      ...pick(data, APPOINTMENT_MOCK_EDITABLE_FIELDS),
      anchor: toAppointmentMockAnchor({ data, mockContext }),
      can_edit: true,
      deletedAt: null,
      description: data.description ?? EMPTY_STRING,
      email_thread: toAppointmentMockEmailThread({ data, mockContext }),
      follow_up: APPOINTMENT_MOCK_FOLLOW_UP_RELEASED,
      history: [
        toAppointmentMockHistoryEntry({
          action: AppointmentHistoryAction.created,
          actor: organizer,
          payload: pick(data, APPOINTMENT_MOCK_CREATED_PAYLOAD_FIELDS),
        }),
      ],
      id: `${APPOINTMENT_MOCK_ID_PREFIX}${numericId}`,
      location: data.location ?? EMPTY_STRING,
      numericId,
      organizer,
      participants: toAppointmentMockParticipants({
        createdAt: new Date().toISOString(),
        data,
        mockContext,
        organizerId: organizer?.id,
      }),
      reminder_offset_minutes: data.reminder_offset_minutes ?? null,
      requestDueDate: getAppointmentMockRequestDueDate({
        dueDate: mockContext.anchors?.request?.due_date ?? null,
        requestId: data.request,
      }),
      restoreJobEnabled: mockContext.restoreJobEnabled ?? false,
      status: CalendarAppointmentStatus.confirmed,
      timezone: mockContext.timezone,
    })

    return {
      data: {
        ...toAppointmentMockDetail({ record }),
        follow_up_coupled: record.follow_up.coupled,
      },
    }
  })
}

export function updateMockAppointment({
  data,
  id,
  mockContext = EMPTY_OBJECT,
}) {
  return sleep(APPOINTMENT_MOCK_LATENCY).then(() => {
    const record = getAppointmentMockRecord(id)

    if (!isNil(data.request) && record.anchor.request) {
      throw getAppointmentMockError({
        data: { code: AppointmentErrorCode.requestImmutable },
        status: HTTP_STATUS_CONFLICT,
      })
    }

    const nextRecord = {
      ...record,
      ...pick(data, APPOINTMENT_MOCK_EDITABLE_FIELDS),
      anchor: getAppointmentMockUpdatedAnchor({ data, mockContext, record }),
      participants: data.participants
        ? toAppointmentMockParticipants({
            createdAt: new Date().toISOString(),
            data,
            mockContext,
            organizerId: record.organizer?.id,
          })
        : record.participants,
      requestDueDate: isNil(data.request)
        ? record.requestDueDate
        : getAppointmentMockRequestDueDate({
            dueDate: mockContext.anchors?.request?.due_date ?? null,
            requestId: data.request,
          }),
      timezone: mockContext.timezone ?? record.timezone,
    }

    checkAppointmentMockTime(nextRecord)

    const isRescheduled =
      !getIsAppointmentMockSameInstant(record.start_at, nextRecord.start_at) ||
      !getIsAppointmentMockSameInstant(record.end_at, nextRecord.end_at)

    const isFollowUpLive = getIsAppointmentMockFollowUpLive(record)

    const isFollowUpReleased =
      isRescheduled && record.follow_up.coupled && !isFollowUpLive

    const savedRecord = saveAppointmentMockRecord({
      ...nextRecord,
      follow_up: isFollowUpReleased
        ? APPOINTMENT_MOCK_FOLLOW_UP_RELEASED
        : record.follow_up,
      history: isRescheduled
        ? [
            toAppointmentMockRescheduledEntry({
              actor: mockContext.user ?? record.organizer,
              nextRecord,
              record,
            }),
            ...record.history,
          ]
        : record.history,
    })

    return {
      data: {
        ...toAppointmentMockDetail({ record: savedRecord }),
        follow_up_prompt: getAppointmentMockUpdatePrompt({
          data,
          isFollowUpLive,
          isRescheduled,
          record: savedRecord,
        }),
        follow_up_released: isFollowUpReleased,
      },
    }
  })
}

export function getMockAppointment({ historyOffset, id }) {
  return sleep(APPOINTMENT_MOCK_LATENCY).then(() => ({
    data: toAppointmentMockDetail({
      historyOffset,
      record: getAppointmentMockRecord(id),
    }),
  }))
}

export function getMockAppointments({ params }) {
  return sleep(APPOINTMENT_MOCK_LATENCY).then(() => {
    if (!getIsAppointmentMockListParamsValid(params)) {
      throw getAppointmentMockError({ status: HTTP_STATUS_BAD_REQUEST })
    }

    return {
      data: getAppointmentMockListPage({
        params,
        records: getAppointmentMockRecords(),
      }),
    }
  })
}
